import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { setLocalChargeStatus, setListViewState } from "../../../../redux/slices/invoiceSlice";
import { usePrivilege } from "../../../../guard/usePrivilege";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";

const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Invoice Number",
  "Discrepancy Status",
  "Discrepancy Amount",
  "Amount",
  "Invoice Total Charges",
  "Shipment Total Charges",
  "Charge Code",
  "Shipment Number",
];

const DISCREPANCY_STYLES = {
  DISCREPANCY:      "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  RESOLVED:         "bg-teal-100 dark:bg-teal-900/40 text-black dark:text-teal-200",
  "NO DISCREPANCY": "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  MISSING:          "bg-yellow-100 dark:bg-yellow-900/40 text-black dark:text-yellow-200",
  DEFAULT:          "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

// Fetch an invoice by number, trying each payToOfficeCode in sequence.
// Returns { invoice, payToCode } or null.
const fetchInvoiceByNumber = async (invoiceNumber, API, token, allOfficeCodes, signal) => {
  for (const payToCode of allOfficeCodes) {
    if (signal?.aborted) return null;
    try {
      const { data } = await axios.get(
        `${API}/invoices/number/${encodeURIComponent(invoiceNumber)}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": payToCode,
          },
          signal,
        }
      );
      return { invoice: data, payToCode };
    } catch (e) {
      if (axios.isCancel(e) || signal?.aborted) return null;
      // 404 / not in this office — try next
    }
  }
  console.warn(`⚠️ Invoice ${invoiceNumber} not found in any office`);
  return null;
};

export default function ChargesAuditTab({
  refreshKey = 0,
  searchQuery = "",
  onUpdateCount = () => {},
  onInvoiceClick = () => {},
  statusFilter = "All Statuses",
  onApplyStatusFilter,
  stateKey = "charges-audit",
  onPaginationUpdate = () => {},
}) {
  const dispatch = useDispatch();

  const overrides    = useSelector((s) => s.invoices.chargeAuditOverrides);
  const listViewState = useSelector((s) => s.invoices.listViewState || {});
  const savedView    = listViewState[stateKey] || {};

  const [charges,     setCharges]     = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [page,        setPage]        = useState(savedView.currentPage   || 1);
  const [size,        setSize]        = useState(savedView.itemsPerPage  || 50);
  const [totalPages,  setTotalPages]  = useState(0);
  const [isLoading,   setIsLoading]   = useState(true);
  const [batchLoading,setBatchLoading]= useState(false);
  const [selected,    setSelected]    = useState([]);
  const [openMenu,    setOpenMenu]    = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const menuRef          = useRef(null);
  const checkboxHeaderRef= useRef(null);
  const bulkMenuRef      = useRef(null);
  const dropdownRef      = useRef(null);
  const [showBulkMenu,   setShowBulkMenu] = useState(false);

  const tableViews = useTableSavedViews({
    pageKey: stateKey || TABLE_PAGE_KEYS.INVOICE_CHARGES_AUDIT,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Invoice Number"],
    getExtraConfig: () => ({ currentPage: page, pageSize: size, filter: statusFilter }),
    onApplyConfig: (config) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setSize(config.pageSize);
      if (config.filter !== undefined) onApplyStatusFilter?.(config.filter);
    },
  });

  const {
    columnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  const { hasPrivilege: canApprove } = usePrivilege("invoices:APPROVE");
  const { hasPrivilege: canReject  } = usePrivilege("invoices:REJECT");

  const officeCode    = Cookies.get("officeCodes") || "DIAMOND-META";
  const allOfficeCodes = officeCode.split(",").map((c) => c.trim());
  const API           = process.env.REACT_APP_API_BASE_URL + "/api";

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const formatMoney = (amount, currency) => {
    if (amount == null || amount === "") return null;
    const n = Number(amount);
    if (Number.isNaN(n)) return null;
    const safe = typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const code = /^[A-Z]{3}$/.test(safe) ? safe : "USD";
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency", currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${code}`;
    }
  };

  const normCode = (c) =>
    (c ?? "").toString().trim().toUpperCase().replace(/^BASE$/, "BAS");

  // ─── Sync list-view state to Redux ────────────────────────────────────────

  useEffect(() => {
    dispatch(setListViewState({ stateKey, currentPage: page, itemsPerPage: size }));
  }, [dispatch, stateKey, page, size]);

  // ─── Keep pagination meta in sync ─────────────────────────────────────────

  useEffect(() => {
    const tp = Math.ceil(filtered.length / size) || 1;
    setTotalPages(tp);
    onPaginationUpdate({
      currentPage: page,
      itemsPerPage: size,
      totalItems: filtered.length,
      totalPages: tp,
    });
  }, [page, size, filtered.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Close portal dropdown on outside click ────────────────────────────────

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ─── Close bulk menu on outside click ─────────────────────────────────────

  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current       && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ─── Main data fetch ───────────────────────────────────────────────────────

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    setIsLoading(true);

    (async () => {
      try {
        const token   = Cookies.get("authToken");
        const apiPage = Math.max(page - 1, 0);
        const headers = {
          "Content-Type":  "application/json",
          Authorization:   `Bearer ${token}`,
          "Office-Code":   officeCode,
        };

        // 1. Parallel initial fetches
        const [{ data: byOffice }, { data: invResp }] = await Promise.all([
          axios.get(`${API}/charges/by-office?page=${apiPage}&size=${size}`, { headers, signal }),
          axios.post(
            `${API}/invoice-charges/search?page=${apiPage}&size=${size}`,
            { query: searchQuery || null },
            { headers, signal }
          ),
        ]);

        if (signal.aborted) return;

        // 2. Fetch all unique invoices ONCE, building:
        //    - statusCache   (invoiceNumber → status string)
        //    - invoiceCache  (invoiceNumber → invoice object)
        //    - shipmentToOfficeMap (shipmentNumber → billToOfficeCode)
        const rawCharges       = invResp.content ?? [];
        const uniqueInvoiceNums = [...new Set(rawCharges.map((c) => c.invoiceNumber).filter(Boolean))];

        const statusCache         = new Map();
        const invoiceCache        = new Map();
        const shipmentToOfficeMap = {};

        await Promise.all(
          uniqueInvoiceNums.map(async (invoiceNumber) => {
            const result = await fetchInvoiceByNumber(invoiceNumber, API, token, allOfficeCodes, signal);
            if (!result) {
              statusCache.set(invoiceNumber, "unknown");
              return;
            }
            const { invoice } = result;
            const status = (invoice?.invoiceStatus || "").toLowerCase();
            statusCache.set(invoiceNumber, status);
            invoiceCache.set(invoiceNumber, invoice);

            const billToCode = invoice.billToOfficeCode;

            // Map shipments from the invoice object
            (invoice.shipments || []).forEach((ship) => {
              if (ship.shipmentNumber) shipmentToOfficeMap[ship.shipmentNumber] = billToCode;
            });

            // Also map from the raw invoice-charges (fallback)
            rawCharges
              .filter((ic) => ic.invoiceNumber === invoiceNumber && ic.shipmentNumber)
              .forEach((ic) => {
                shipmentToOfficeMap[ic.shipmentNumber] = billToCode;
              });
          })
        );

        if (signal.aborted) return;

        // 3. Filter out draft / rejected invoices
        let invoiceList = rawCharges.filter((charge) => {
          if (!charge.invoiceNumber) return true;
          const status = statusCache.get(charge.invoiceNumber);
          if (!status || status === "unknown") return true;
          return status !== "draft" && status !== "rejected";
        });

        if (invoiceList.length === 0 && rawCharges.length > 0) {
          console.warn("Status filter removed all charges; using unfiltered list.");
          invoiceList = rawCharges;
        }

        // 4. Fetch shipments using the pre-built billToOfficeCode mapping
        const shipmentNumbers = [...new Set(invoiceList.map((x) => x.shipmentNumber).filter(Boolean))];

        const shipments = (
          await Promise.all(
            shipmentNumbers.map(async (sn) => {
              const billToOfficeCode = shipmentToOfficeMap[sn];
              if (!billToOfficeCode) {
                console.warn(`⚠️ No billToOfficeCode for shipment ${sn}`);
                return null;
              }
              try {
                const { data } = await axios.get(
                  `${API}/shipments/detailed-shipment/${encodeURIComponent(sn)}`,
                  {
                    headers: { Authorization: `Bearer ${token}`, "Office-Code": billToOfficeCode },
                    signal,
                  }
                );
                return data;
              } catch (e) {
                if (axios.isCancel(e) || signal.aborted) return null;
                console.warn(`⚠️ Could not fetch shipment ${sn}:`, e?.response?.data || e.message);
                return null;
              }
            })
          )
        ).filter(Boolean);

        if (signal.aborted) return;

        // 5. Build charge maps from shipment data
        const shipmentCharges = shipments.flatMap((sh) =>
          (sh.charges || []).map((sc) => ({
            shipmentNumber: sh.shipmentNumber,
            amount: Number(sc.amount) || 0,
            code: normCode(sc.code),
          }))
        );

        const shipmentChargeMap = shipmentCharges.reduce((m, r) => {
          const k = `${r.shipmentNumber}|${r.code}`;
          m[k] = (m[k] || 0) + r.amount;
          return m;
        }, {});

        const shipmentTotals = shipmentCharges.reduce((m, r) => {
          m[r.shipmentNumber] = (m[r.shipmentNumber] || 0) + r.amount;
          return m;
        }, {});

        const invoiceTotals = invoiceList.reduce((m, x) => {
          m[x.shipmentNumber] = (m[x.shipmentNumber] || 0) + (Number(x.chargeAmount) || 0);
          return m;
        }, {});

        // 6. Annotate each charge with discrepancy info
        const annotated = [...invoiceList]
          .sort((a, b) => {
            const dA = new Date(a.updatedDate || a.createdDate || 0);
            const dB = new Date(b.updatedDate || b.createdDate || 0);
            return dB - dA;
          })
          .map((ic) => {
            const code        = normCode(ic.chargeCode || ic.code);
            const key         = `${ic.shipmentNumber}|${code}`;
            const shipmentAmt = shipmentChargeMap[key] ?? 0;
            const invoiceAmt  = Number(ic.chargeAmount) || 0;

            const storedDisc  = ic.discrepancyAmount;
            const computedDisc= invoiceAmt - shipmentAmt;
            const diff        =
              storedDisc !== null && storedDisc !== undefined && !isNaN(Number(storedDisc))
                ? Number(storedDisc)
                : computedDisc;

            let discType = ic.discrepancyStatus;
            if (discType == null) discType = diff === 0 ? "No Discrepancy" : "Discrepancy";
            if (overrides[ic.chargeId]) discType = overrides[ic.chargeId];

            const resolved =
              Math.abs((shipmentTotals[ic.shipmentNumber] || 0) - (invoiceTotals[ic.shipmentNumber] || 0)) < 1e-6
                ? "Resolved"
                : "";

            return {
              ...ic,
              discrepancyStatus:   discType,
              shipmentResolved:    resolved,
              shipmentTotalCharge: shipmentTotals[ic.shipmentNumber] || 0,
              discrepancyAmount:   diff,
              invoiceTotalCharge:  invoiceTotals[ic.shipmentNumber] || 0,
            };
          });

        setCharges(annotated);
        onUpdateCount(invResp.totalElements ?? annotated.length);
      } catch (e) {
        if (axios.isCancel(e) || signal.aborted) return;
        console.error("ChargesAuditTab fetch error:", e);
        setCharges([]);
        setTotalPages(0);
        onUpdateCount(0);
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
    })();

    return () => abortController.abort();
  }, [refreshKey, searchQuery, page, size, officeCode, overrides]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Client-side filter ───────────────────────────────────────────────────

  useEffect(() => {
    let f = charges;
    if (statusFilter !== "All Statuses") {
      f = f.filter((c) => c.discrepancyStatus === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      f = f.filter(
        (c) =>
          (c.shipmentNumber  || "").toLowerCase().includes(q) ||
          (c.chargeCode      || "").toLowerCase().includes(q) ||
          (c.discrepancyStatus || "").toLowerCase().includes(q) ||
          (c.shipmentResolved  || "").toLowerCase().includes(q)
      );
    }
    setFiltered(f);
  }, [charges, statusFilter, searchQuery]);

  // ─── Invoice click handler ────────────────────────────────────────────────

  const handleChargeClick = async (charge) => {
    if (!charge?.invoiceNumber) return notify.warning("This charge has no invoice number.");
    notify.info({ title: "Loading invoice..." });

    try {
      const token = Cookies.get("authToken");
      const result = await fetchInvoiceByNumber(charge.invoiceNumber, API, token, allOfficeCodes);
      if (!result) return notify.error(`Invoice ${charge.invoiceNumber} not found.`);

      const { invoice } = result;
      const { data: fullInvoice } = await axios.get(`${API}/invoices/${invoice.uniqueId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${token}`,
          "Office-Code":  invoice.billToOfficeCode,
        },
      });

      onInvoiceClick({
        ...fullInvoice,
        uniqueId: fullInvoice.uniqueId ?? fullInvoice.id ?? fullInvoice.invoiceId ?? null,
      });
    } catch (e) {
      console.error("Error opening invoice:", e);
      if (e?.response?.status === 404)      notify.error(`Invoice ${charge.invoiceNumber} not found.`);
      else if (e?.response?.status === 500) notify.error("Server error loading invoice. Please try again.");
      else                                  notify.error("Couldn't open invoice for this charge.");
    }
  };

  // ─── Batch status update ──────────────────────────────────────────────────

  const handleBatchStatusUpdate = async (ids, status) => {
    if (!ids?.length) return;
    setBatchLoading(true);
    try {
      const token = Cookies.get("authToken");
      const { data: updatedCharges } = await axios.put(
        `${API}/invoice-charges/discrepancy-status`,
        { ids, status },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${token}`,
            "Office-Code":  officeCode,
          },
        }
      );

      updatedCharges.forEach((charge) =>
        dispatch(setLocalChargeStatus({ id: charge.chargeId, status: charge.discrepancyStatus }))
      );

      setCharges((prev) =>
        prev.map((c) => {
          const updated = updatedCharges.find((uc) => uc.chargeId === c.chargeId);
          return updated
            ? { ...c, discrepancyStatus: updated.discrepancyStatus, invoiceNumber: updated.invoiceNumber }
            : c;
        })
      );
      setSelected([]);
    } catch (e) {
      console.error("Could not update charge status:", e);
    } finally {
      setBatchLoading(false);
      setOpenMenu(null);
    }
  };

  const handleApproveCharge = (id)  => handleBatchStatusUpdate([id], "Resolved");
  const handleRejectCharge  = (id)  => handleBatchStatusUpdate([id], "Discrepancy");
  const handleBatchApprove  = ()    => selected.length && handleBatchStatusUpdate(selected, "Resolved");
  const handleBatchReject   = ()    => handleBatchStatusUpdate(selected, "Discrepancy");

  const handleActionClick = (id, disabled, e) => {
    if (disabled) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  // ─── Row builder ──────────────────────────────────────────────────────────

  const rows = filtered.map((c) => ({
    Selection:             c.chargeId,
    Action:                c,
    "Invoice Number":      c.invoiceNumber,
    "Discrepancy Status": (
      <span className={`px-2.5 py-[2px] rounded font-medium text-sm tracking-wide ${
        DISCREPANCY_STYLES[(c.discrepancyStatus || "").toUpperCase()] || DISCREPANCY_STYLES.DEFAULT
      }`}>
        {(c.discrepancyStatus || "").toUpperCase()}
      </span>
    ),
    "Discrepancy Amount": (
      <span className={
        c.discrepancyAmount !== null && c.discrepancyAmount !== 0
          ? "text-red-600 dark:text-red-400 font-medium"
          : "text-gray-800 dark:text-gray-200"
      }>
        {c.discrepancyAmount !== null && c.discrepancyAmount > 0
          ? `+${formatMoney(c.discrepancyAmount, c.chargeCurrency)}`
          : c.discrepancyAmount !== null && c.discrepancyAmount < 0
          ? formatMoney(c.discrepancyAmount, c.chargeCurrency)
          : c.discrepancyAmount !== null && c.discrepancyAmount === 0
          ? formatMoney(0, c.chargeCurrency)
          : "-"}
      </span>
    ),
    Amount:                    formatMoney(c.chargeAmount, c.chargeCurrency || "USD"),
    "Invoice Total Charges":   formatMoney(c.invoiceTotalCharge, c.chargeCurrency),
    "Shipment Total Charges":  formatMoney(c.shipmentTotalCharge, c.chargeCurrency),
    "Charge Code":             c.code,
    "Shipment Number":         c.shipmentNumber,
  }));

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading || batchLoading ? (
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message={isLoading ? "Loading charges..." : "Updating charges..."}
        />
      ) : (
        <>
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={viewsError}
              isDirty={isDirty}
              allDataHeaders={TABLE_HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          </div>
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={rows}
              initialColumnWidths={columnWidths}
              onColumnResize={onColumnResize}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              selectedRows={selected}
              totalValidRows={rows.length}
              onSelectAll={(checked) => setSelected(checked ? filtered.map((c) => c.chargeId) : [])}
              renderCheckboxActions={() => setShowBulkMenu((v) => !v)}
              checkboxHeaderRef={checkboxHeaderRef}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id     = row.Selection;
                const isOpen = openMenu === id;
                const status = (row.Action.discrepancyStatus || "").toUpperCase();
                const hideActions = status === "NO DISCREPANCY" || status === "RESOLVED";
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                const actions = [];
                if (!hideActions) {
                  if (canApprove) actions.push({ icon: "Check", label: "Approve Charge", color: "green", action: () => { handleApproveCharge(id); setOpenMenu(null); } });
                  if (canReject)  actions.push({ icon: "X",     label: "Reject Charge",  color: "red",   action: () => { handleRejectCharge(id);  setOpenMenu(null); } });
                }

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                      style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selected.includes(id)}
                        onChange={() =>
                          setSelected((s) =>
                            s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
                          )
                        }
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                      style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className={`p-1 relative w-full transition-none ${
                          hideActions || actions.length === 0
                            ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
                        }`}
                        disabled={hideActions || actions.length === 0}
                        onClick={(e) => handleActionClick(id, hideActions || actions.length === 0, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && actions.length > 0 &&
                      ReactDOM.createPortal(
                        <div
                          ref={dropdownRef}
                          style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                        >
                          <ActionMenu
                            rowId={id}
                            isOpen
                            moduleName="Charge"
                            onClose={() => setOpenMenu(null)}
                            actions={actions}
                          />
                        </div>,
                        document.body
                      )}

                    {/* Columns 2+ — Data */}
                    {dataHeadersToRender.map((header) => {
                      return (
                        <td
                          key={header}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(header).className}`}
                          style={{
                            ...getStickyCellProps(header).style,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        "200px",
                            minWidth:     "200px",
                            maxWidth:     "200px",
                          }}
                          onDoubleClick={() => handleChargeClick(row.Action)}
                        >
                          {row[header]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>

            {showBulkMenu && checkboxHeaderRef.current && (
              <div
                ref={bulkMenuRef}
                className="absolute z-[1000]"
                style={{
                  top:  checkboxHeaderRef.current.getBoundingClientRect().bottom + window.scrollY + 6,
                  left: checkboxHeaderRef.current.getBoundingClientRect().left   + window.scrollX,
                }}
              >
                <ActionMenu
                  rowId="bulk"
                  isOpen
                  onClose={() => setShowBulkMenu(false)}
                  moduleName="Charge"
                  placement="header"
                  actions={[
                    { icon: "Check", label: "Approve Selected", color: "green", action: () => { handleBatchApprove(); setShowBulkMenu(false); } },
                    { icon: "X",     label: "Reject Selected",  color: "red",   action: () => { handleBatchReject();  setShowBulkMenu(false); } },
                  ]}
                />
              </div>
            )}
          </div>

          <GlobalPagination
            label="Charges per page"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={size}
            onItemsPerPageChange={(newSize) => { setSize(newSize); setPage(1); }}
          />
        </>
      )}
    </div>
  );
}
